import React, { useState } from 'react';
import {
  Typography,
  TextField,
  Button,
  ButtonGroup,
  Box,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  EmojiEmotions,
  Send,
} from '@mui/icons-material';
import EmojiPicker, { EmojiClickData } from 'emoji-picker-react';
import QRCode from 'qrcode';
import DOMPurify from 'dompurify';
import { v4 as uuidv4 } from 'uuid';
import { storage } from '../config/firebase';
import { ref, uploadString, getDownloadURL } from 'firebase/storage';

interface TextToQRProps {
  onQRGenerated: (imageUrl: string) => void;
}

const TextToQR: React.FC<TextToQRProps> = ({ onQRGenerated }) => {
  const [text, setText] = useState('');
  const [title, setTitle] = useState('');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFormat = (format: 'bold' | 'italic' | 'underline') => {
    const textarea = document.querySelector('textarea');
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = text.substring(start, end);

    let formattedText = '';
    switch (format) {
      case 'bold':
        formattedText = `<strong>${selectedText}</strong>`;
        break;
      case 'italic':
        formattedText = `<em>${selectedText}</em>`;
        break;
      case 'underline':
        formattedText = `<u>${selectedText}</u>`;
        break;
    }

    const newText = text.substring(0, start) + formattedText + text.substring(end);
    setText(newText);
  };

  const onEmojiClick = (emojiData: EmojiClickData) => {
    const textarea = document.querySelector('textarea');
    if (!textarea) return;

    const start = textarea.selectionStart;
    const newText = text.substring(0, start) + emojiData.emoji + text.substring(start);
    setText(newText);
    setShowEmojiPicker(false);
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError(null);

      // Sanitize the content
      const sanitizedTitle = DOMPurify.sanitize(title);
      const sanitizedText = DOMPurify.sanitize(text);
      const content = `
                    <div>
                        <h1>${sanitizedTitle}</h1>
                        <div>${sanitizedText}</div>
                    </div>`;

      // Generate unique identifier
      const uniqueId = uuidv4();
      const fileName = `html_files/${uniqueId}.html`;

      // Upload to Firebase Storage
      const storageRef = ref(storage, fileName);
      await uploadString(storageRef, content);

      // Get the download URL
      const downloadUrl = await getDownloadURL(storageRef);

      // parse url
      const base_url = process.env.REACT_APP_BASE_URL;
      const bucket = 'html_files'
      const token = downloadUrl.split('=').slice(-1)[0];
      const full_url = `${base_url}/page.html?bucket=${bucket}&file_id=${uniqueId}&token=${token}`;
      // Generate QR code with the download URL
      const qrDataUrl = await QRCode.toDataURL(full_url, {
        errorCorrectionLevel: 'H',
        margin: 1,
        width: 300,
        color: {
          dark: '#000000',
          light: '#ffffff'
        }
      });

      onQRGenerated(qrDataUrl);
    } catch (error) {
      console.error('Error generating QR code:', error);
      setError('Error al generar el código QR. Por favor, intente nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Typography variant="h4" gutterBottom>
        Editor de Texto
      </Typography>

      <TextField
        fullWidth
        label="Título"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        variant="outlined"
        sx={{ mb: 2 }}
      />

      <TextField
        fullWidth
        multiline
        rows={10}
        value={text}
        onChange={(e) => setText(e.target.value)}
        variant="outlined"
        sx={{ mb: 2 }}
      />

      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
        <ButtonGroup variant="contained" sx={{ mr: 2 }}>
          <Button onClick={() => handleFormat('bold')}>
            <FormatBold />
          </Button>
          <Button onClick={() => handleFormat('italic')}>
            <FormatItalic />
          </Button>
          <Button onClick={() => handleFormat('underline')}>
            <FormatUnderlined />
          </Button>
        </ButtonGroup>

        <Button
          variant="contained"
          onClick={() => setShowEmojiPicker(!showEmojiPicker)}
          startIcon={<EmojiEmotions />}
          sx={{ height: '36px' }}
        >
          Emoji
        </Button>
      </Box>

      {showEmojiPicker && (
        <Box sx={{ position: 'absolute', zIndex: 1 }}>
          <EmojiPicker onEmojiClick={onEmojiClick} />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Button
        variant="contained"
        color="primary"
        size="large"
        onClick={handleSubmit}
        disabled={loading || !text}
        startIcon={loading ? <CircularProgress size={20} /> : <Send />}
        fullWidth
      >
        {loading ? 'Generando...' : 'Generar QR'}
      </Button>
    </>
  );
};

export default TextToQR; 